<template>

  <!-- ══════════════════════════════════════════
       DESKTOP HERO  (hidden on mobile ≤768px)
  ══════════════════════════════════════════ -->
  <section class="hero">
    <!-- Animated gradient blobs -->
    <div class="hero-bg" aria-hidden="true">
      <div class="blob blob-1"></div>
      <div class="blob blob-2"></div>
      <div class="blob blob-3"></div>
      <div class="blob blob-4"></div>
    </div>

    <!-- Floating Arabic calligraphy letters -->
    <div class="math-symbols" aria-hidden="true">
      <span class="ms ms-1">ب</span>
      <span class="ms ms-2">ن</span>
      <span class="ms ms-3">ل</span>
      <span class="ms ms-4">م</span>
      <span class="ms ms-5">ع</span>
      <span class="ms ms-6">ص</span>
      <span class="ms ms-7">ق</span>
      <span class="ms ms-8">ر</span>
      <span class="ms ms-9">ك</span>
      <span class="ms ms-10">ح</span>
      <span class="ms ms-11">أ</span>
      <span class="ms ms-12">و</span>
      <span class="ms ms-13">ف</span>
      <span class="ms ms-14">غ</span>
    </div>

    <div class="hero-container">
      <!-- Right: Teacher info -->
      <div class="hero-info">
        <div class="hero-badge">
          <span class="badge-dot"></span>
          ✒️ منصة اللغة العربية الأولى
        </div>

        <h1 class="hero-name" v-if="auth">أهلاً، {{ userName }} 👋</h1>
        <h1 class="hero-name" v-else>أ/ طارق<br />المحلاوي</h1>

        <p class="hero-subtitle">مدرس اللغة العربية للمرحلتين الإعدادية والثانوية</p>

        <p class="hero-desc" v-if="!auth">
          انضم إلى آلاف الطلاب الذين يتقنون اللغة العربية في النحو والبلاغة والأدب،
          مع شرح مبسط وأسلوب حديث.
        </p>
        <p class="hero-desc" v-else>
          أكمل رحلتك التعليمية — دروسك ونتائجك في انتظارك
          <span class="level-chip">{{ educationLevelLabel }}</span>
        </p>

        <!-- CTAs for guests -->
        <div class="hero-actions" v-if="!auth">
          <router-link to="/login" class="btn-hero-primary">✒️ ابدأ التعلم الآن</router-link>
          <a href="https://wa.me/+201021631271" target="_blank" class="btn-hero-wa">
            <i class="pi pi-whatsapp"></i> تواصل معنا
          </a>
        </div>

        <!-- CTAs for authenticated -->
        <div class="hero-actions" v-else>
          <router-link to="/contents" class="btn-hero-primary">📚 محتوى الحصص</router-link>
          <router-link to="/exams?type=online" class="btn-hero-secondary">📝 الامتحانات</router-link>
        </div>

        <!-- Social proof (guests only) -->
        <div class="social-proof" v-if="!auth">
          <div class="proof-avatars">
            <div class="pa pa-1">أ</div>
            <div class="pa pa-2">م</div>
            <div class="pa pa-3">س</div>
            <div class="pa pa-4">ي</div>
            <div class="pa pa-5">ف</div>
          </div>
          <span>+2000 طالب يتعلمون الآن</span>
        </div>
      </div>

      <!-- Left: Avatar + floating stat cards -->
      <div class="hero-visual">
        <div class="avatar-scene">
          <div class="avatar-glow"></div>
          <div class="avatar-ring">
            <img src="@/assets/images/logo.png" alt="أ/طارق المحلاوي" />
          </div>
          <div class="orbit orbit-1"><div class="orbit-dot"></div></div>
          <div class="orbit orbit-2"><div class="orbit-dot orbit-dot-2"></div></div>
        </div>

        <div class="stat-card stat-card-1">
          <div class="stat-card-icon">📖</div>
          <div class="stat-card-body">
            <div class="stat-card-num">2000+</div>
            <div class="stat-card-label">طالب مسجّل</div>
          </div>
        </div>
        <div class="stat-card stat-card-2">
          <div class="stat-card-icon">✒️</div>
          <div class="stat-card-body">
            <div class="stat-card-num">15+</div>
            <div class="stat-card-label">سنة خبرة</div>
          </div>
        </div>
        <div class="stat-card stat-card-3">
          <div class="stat-card-icon">⭐</div>
          <div class="stat-card-body">
            <div class="stat-card-num">95%</div>
            <div class="stat-card-label">نسبة الرضا</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Scroll indicator -->
    <div class="scroll-indicator">
      <div class="scroll-mouse"><div class="scroll-wheel"></div></div>
      <span>اكتشف أكثر</span>
    </div>
  </section>

  <!-- ══════════════════════════════════════════
       MOBILE APP HOME SCREEN  (hidden on desktop >768px)
       Feels like opening a premium learning app.
  ══════════════════════════════════════════ -->
  <section class="mah" dir="rtl">

    <!-- ── AUTH LAYOUT ── -->
    <template v-if="auth">

      <!-- Greeting banner -->
      <div class="mah-greeting">
        <div class="mah-greeting-text">
          <span class="mah-hello">مرحباً مجدداً،</span>
          <span class="mah-name">{{ userName }} 👋</span>
        </div>
        <span class="mah-level-chip" v-if="educationLevelLabel">{{ educationLevelLabel }}</span>
      </div>

      <!-- Compact teacher bar -->
      <div class="mah-teacher-bar">
        <div class="mah-tb-avatar">
          <img src="@/assets/images/favicon.png" alt="أ/طارق المحلاوي" />
        </div>
        <div class="mah-tb-info">
          <div class="mah-tb-name">أ/ طارق المحلاوي</div>
          <div class="mah-tb-sub">مدرس اللغة العربية</div>
        </div>
        <div class="mah-tb-stats">
          <div class="mah-tb-stat">
            <span>2000+</span>
            <small>طالب</small>
          </div>
          <div class="mah-tb-divider"></div>
          <div class="mah-tb-stat">
            <span>95%</span>
            <small>نجاح</small>
          </div>
        </div>
      </div>

      <!-- Quick access grid -->
      <div class="mah-quick-section">
        <div class="mah-sec-title">وصول سريع</div>
        <div class="mah-quick-grid">
          <router-link to="/contents" class="mah-qcard mah-qc-lessons">
            <span class="mah-qc-icon">📚</span>
            <span class="mah-qc-label">محتوى الحصص</span>
            <span class="mah-qc-sub">فيديوهات وملفات</span>
            <span class="mah-qc-arrow">←</span>
          </router-link>
          <router-link to="/exams?type=online" class="mah-qcard mah-qc-exams">
            <span class="mah-qc-icon">📝</span>
            <span class="mah-qc-label">الامتحانات</span>
            <span class="mah-qc-sub">أونلاين فوري</span>
            <span class="mah-qc-arrow">←</span>
          </router-link>
          <router-link to="/exams?type=homework" class="mah-qcard mah-qc-hw">
            <span class="mah-qc-icon">🏠</span>
            <span class="mah-qc-label">الواجب المنزلي</span>
            <span class="mah-qc-sub">تدريبات يومية</span>
            <span class="mah-qc-arrow">←</span>
          </router-link>
          <router-link to="/my_exams" class="mah-qcard mah-qc-results">
            <span class="mah-qc-icon">📊</span>
            <span class="mah-qc-label">نتائجي</span>
            <span class="mah-qc-sub">تابع تقدمك</span>
            <span class="mah-qc-arrow">←</span>
          </router-link>
        </div>
      </div>

    </template>

    <!-- ── GUEST LAYOUT ── -->
    <template v-else>

      <!-- Teacher hero card -->
      <div class="mah-guest-hero">
        <div class="mah-gh-blobs" aria-hidden="true">
          <div class="mah-gh-blob-1"></div>
          <div class="mah-gh-blob-2"></div>
        </div>

        <!-- Arabic calligraphy letters (subtle) -->
        <div class="mah-gh-math" aria-hidden="true">
          <span>ب</span><span>ن</span><span>ل</span><span>م</span>
          <span>ع</span><span>ص</span><span>ق</span><span>ر</span>
        </div>

        <div class="mah-gh-inner">
          <div class="mah-gh-avatar">
            <img src="@/assets/images/favicon.png" alt="أ/طارق المحلاوي" />
          </div>
          <div class="mah-gh-badge">
            <span class="mah-gh-dot"></span>
            ✒️ منصة اللغة العربية الأولى
          </div>
          <h1 class="mah-gh-name">أ/ طارق المحلاوي</h1>
          <p class="mah-gh-sub">مدرس اللغة العربية للمرحلتين الإعدادية والثانوية</p>
          <div class="mah-gh-stats">
            <div class="mah-gh-stat"><span>2000+</span><small>طالب</small></div>
            <div class="mah-gh-stat-div"></div>
            <div class="mah-gh-stat"><span>15+</span><small>خبرة</small></div>
            <div class="mah-gh-stat-div"></div>
            <div class="mah-gh-stat"><span>95%</span><small>رضا</small></div>
          </div>
        </div>
      </div>

      <!-- CTA actions -->
      <div class="mah-guest-cta">
        <router-link to="/login" class="mah-btn mah-btn-primary">
          ✒️ ابدأ التعلم الآن
        </router-link>
        <a href="https://wa.me/+201021631271" target="_blank" class="mah-btn mah-btn-wa">
          <i class="pi pi-whatsapp"></i> تواصل مع الأستاذ
        </a>
        <div class="mah-social-proof">
          <div class="mah-sp-avatars">
            <div class="mah-sp-av mah-sp-1">أ</div>
            <div class="mah-sp-av mah-sp-2">م</div>
            <div class="mah-sp-av mah-sp-3">س</div>
          </div>
          <span>+2000 طالب يتعلمون الآن</span>
        </div>
      </div>

      <!-- Features preview grid -->
      <div class="mah-features">
        <div class="mah-sec-title">لماذا منصتنا؟</div>
        <div class="mah-feat-grid">
          <div class="mah-feat-card">
            <span class="mah-fi mah-fi-purple">🎬</span>
            <div>
              <div class="mah-feat-label">دروس فيديو</div>
              <div class="mah-feat-desc">شرح واضح بجودة عالية</div>
            </div>
          </div>
          <div class="mah-feat-card">
            <span class="mah-fi mah-fi-blue">📝</span>
            <div>
              <div class="mah-feat-label">امتحانات تفاعلية</div>
              <div class="mah-feat-desc">تصحيح فوري ومراجعة</div>
            </div>
          </div>
          <div class="mah-feat-card">
            <span class="mah-fi mah-fi-green">📊</span>
            <div>
              <div class="mah-feat-label">متابعة التقدم</div>
              <div class="mah-feat-desc">تتبع أداءك ونتائجك</div>
            </div>
          </div>
          <div class="mah-feat-card">
            <span class="mah-fi mah-fi-orange">💬</span>
            <div>
              <div class="mah-feat-label">دعم مستمر</div>
              <div class="mah-feat-desc">تواصل مباشر مع الأستاذ</div>
            </div>
          </div>
        </div>
      </div>

    </template>

  </section>

</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  auth: { type: Boolean, default: false },
  user: { type: Object, default: null },
});

const userName = computed(() => props.user?.name?.split(' ')[0] || '');
const educationLevelLabel = computed(() => props.user?.academic_year?.name || '');
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;800;900&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

/* ══════════════════════════════════════════
   DESKTOP HERO — Black & Gold Identity
══════════════════════════════════════════ */

/* ── Shared buttons ── */
.btn-hero-primary, .btn-hero-secondary, .btn-hero-wa {
  display: inline-flex; align-items: center; justify-content: center; gap: .6rem;
  padding: .9rem 2rem; border-radius: 99px;
  font-weight: 700; font-size: 1rem; text-decoration: none;
  transition: all .3s cubic-bezier(.4,0,.2,1); border: none; cursor: pointer;
  font-family: 'Cairo', sans-serif;
}
.btn-hero-primary  { background: linear-gradient(135deg,#D4A017,#B8860B); color:#0F0F0F; box-shadow:0 12px 32px rgba(212,160,23,.35); }
.btn-hero-primary:hover  { transform:translateY(-3px); box-shadow:0 20px 50px rgba(212,160,23,.5); color:#0F0F0F; }
.btn-hero-secondary { background:transparent; color:#D4A017; border:2px solid rgba(212,160,23,.4) !important; }
.btn-hero-secondary:hover { background:rgba(212,160,23,.08); border-color:#D4A017 !important; transform:translateY(-2px); color:#D4A017; }
.btn-hero-wa { background:linear-gradient(135deg,#25d366,#128C7E); color:#fff; box-shadow:0 12px 30px rgba(37,211,102,.25); }
.btn-hero-wa:hover { transform:translateY(-3px); box-shadow:0 20px 45px rgba(37,211,102,.4); color:#fff; }

/* ── Hero ── */
.hero {
  min-height: 100vh;
  background: linear-gradient(135deg,#080808 0%,#111111 45%,#0A0A0A 100%);
  position: relative; overflow: hidden;
  display: flex; flex-direction: column; align-items: stretch;
  font-family: 'Cairo', sans-serif; direction: rtl;
}

/* Gold blobs */
.hero-bg { position:absolute; inset:0; pointer-events:none; }
.blob { position:absolute; border-radius:50%; filter:blur(80px); opacity:.25; animation:blobMove 8s ease-in-out infinite; }
.blob-1 { width:600px;height:600px; background:radial-gradient(circle,#D4A017,#8B6508); top:-200px; right:-100px; }
.blob-2 { width:500px;height:500px; background:radial-gradient(circle,#B8860B,#6B5000); bottom:-150px;left:-100px; animation-delay:-3s; }
.blob-3 { width:400px;height:400px; background:radial-gradient(circle,#D4A017,#B8860B); top:40%;left:35%; animation-delay:-5s; opacity:.12; }
.blob-4 { width:300px;height:300px; background:radial-gradient(circle,#F0C040,#D4A017); top:60%;right:20%; animation-delay:-2s; opacity:.15; }
@keyframes blobMove { 0%,100%{transform:scale(1) translate(0,0)} 33%{transform:scale(1.1) translate(30px,-30px)} 66%{transform:scale(.9) translate(-20px,20px)} }

/* Islamic-inspired geometric grid overlay */
.hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    repeating-linear-gradient(45deg, transparent, transparent 48px, rgba(212,160,23,.025) 48px, rgba(212,160,23,.025) 50px),
    repeating-linear-gradient(-45deg, transparent, transparent 48px, rgba(212,160,23,.025) 48px, rgba(212,160,23,.025) 50px);
  pointer-events: none;
  z-index: 0;
}

/* Arabic/math symbols */
.math-symbols { position:absolute;inset:0;pointer-events:none;overflow:hidden;z-index:0; }
.ms { position:absolute;color:rgba(212,160,23,.07);font-size:3rem;font-family:'Cairo',sans-serif;font-weight:700;animation:mathFloat 6s ease-in-out infinite;user-select:none; }
.ms-1  { top:10%;right:8%;font-size:4rem;animation-duration:7s; }
.ms-2  { top:25%;right:25%;font-size:2.5rem;animation-delay:-1s;animation-duration:8s; }
.ms-3  { top:60%;right:15%;font-size:3.5rem;animation-delay:-2s;animation-duration:6s; }
.ms-4  { top:15%;left:12%;font-size:3rem;animation-delay:-3s;animation-duration:9s; }
.ms-5  { top:50%;left:8%;font-size:4.5rem;animation-delay:-1.5s;animation-duration:7s; }
.ms-6  { top:75%;left:20%;font-size:2.8rem;animation-delay:-4s;animation-duration:8s; }
.ms-7  { top:35%;left:40%;font-size:2rem;animation-delay:-2.5s;animation-duration:10s; }
.ms-8  { top:80%;right:35%;font-size:3rem;animation-delay:-.5s;animation-duration:7s; }
.ms-9  { top:5%;left:50%;font-size:2.5rem;animation-delay:-3.5s;animation-duration:9s; }
.ms-10 { top:90%;right:55%;font-size:2rem;animation-delay:-1s;animation-duration:8s; }
.ms-11 { top:40%;right:5%;font-size:2.5rem;animation-delay:-4.5s;animation-duration:6s; }
.ms-12 { top:65%;left:45%;font-size:3rem;animation-delay:-2s;animation-duration:11s; }
.ms-13 { top:20%;left:30%;font-size:2rem;animation-delay:-3s;animation-duration:8s; }
.ms-14 { top:85%;left:65%;font-size:3.5rem;animation-duration:7s; }
@keyframes mathFloat { 0%,100%{transform:translate(0,0) rotate(0deg);opacity:.07} 25%{transform:translate(15px,-20px) rotate(8deg);opacity:.13} 75%{transform:translate(-10px,15px) rotate(-6deg);opacity:.04} }

/* Hero layout */
.hero-container {
  max-width:1200px;margin:0 auto;width:100%;
  padding:120px 2rem 80px;
  display:grid;grid-template-columns:1fr 1fr;gap:3rem;align-items:center;
  position:relative;z-index:1;flex:1;
}
.hero-info { display:flex;flex-direction:column;gap:1.5rem; }

.hero-badge {
  display:inline-flex;align-items:center;gap:.5rem;
  background:rgba(212,160,23,.1);backdrop-filter:blur(10px);
  border:1px solid rgba(212,160,23,.3);border-radius:99px;
  padding:.4rem 1.1rem;color:rgba(212,160,23,.95);font-size:.85rem;font-weight:600;
  width:fit-content;animation:fadeSlideIn .8s ease both;
}
.badge-dot { width:8px;height:8px;border-radius:50%;background:#D4A017;box-shadow:0 0 8px #D4A017;animation:pulseGold 1.5s infinite; }
@keyframes pulseGold { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.4);opacity:.6} }

.hero-name { font-size:clamp(2.2rem,5vw,3.5rem);font-weight:900;color:#F0EDE4;line-height:1.15;letter-spacing:-.02em;animation:fadeSlideIn .8s ease .1s both; }
.hero-subtitle { font-size:1.1rem;color:rgba(240,237,228,.65);font-weight:500;animation:fadeSlideIn .8s ease .2s both; }
.hero-desc { font-size:1rem;color:rgba(240,237,228,.55);line-height:1.8;max-width:480px;animation:fadeSlideIn .8s ease .3s both; }
.level-chip { display:inline-block;background:rgba(212,160,23,.15);color:#D4A017;border:1px solid rgba(212,160,23,.3);border-radius:99px;padding:.15rem .75rem;font-size:.82rem;font-weight:700;margin-right:.5rem; }
.hero-actions { display:flex;gap:1rem;flex-wrap:wrap;animation:fadeSlideIn .8s ease .4s both; }
.hero-actions .btn-hero-primary, .hero-actions .btn-hero-secondary, .hero-actions .btn-hero-wa { width:auto; }

.social-proof { display:flex;align-items:center;gap:.75rem;animation:fadeSlideIn .8s ease .5s both;color:rgba(240,237,228,.55);font-size:.88rem; }
.proof-avatars { display:flex; }
.pa { width:34px;height:34px;border-radius:50%;border:2px solid rgba(212,160,23,.3);display:flex;align-items:center;justify-content:center;font-size:.85rem;font-weight:700;color:#0F0F0F;margin-left:-8px; }
.pa:first-child { margin-left:0; }
.pa-1 { background:linear-gradient(135deg,#D4A017,#B8860B); }
.pa-2 { background:linear-gradient(135deg,#C89010,#A07008); }
.pa-3 { background:linear-gradient(135deg,#E0B020,#C09010); }
.pa-4 { background:linear-gradient(135deg,#B8860B,#8B6508); }
.pa-5 { background:linear-gradient(135deg,#D4A017,#F0C040); }

/* Hero visual */
.hero-visual { position:relative;display:flex;align-items:center;justify-content:center;height:500px;animation:fadeSlideIn .8s ease .2s both; }
.avatar-scene { position:relative;display:flex;align-items:center;justify-content:center; }
.avatar-glow { position:absolute;width:280px;height:280px;border-radius:50%;background:radial-gradient(circle,rgba(212,160,23,.45) 0%,transparent 70%);animation:glowPulse 3s ease-in-out infinite; }
@keyframes glowPulse { 0%,100%{transform:scale(1);opacity:.5} 50%{transform:scale(1.2);opacity:.85} }
.avatar-ring { width:200px;height:200px;border-radius:50%;padding:5px;background:linear-gradient(135deg,#D4A017,#8B6508,#F0C040);box-shadow:0 0 60px rgba(212,160,23,.5),0 0 120px rgba(212,160,23,.2);animation:floatY 4s ease-in-out infinite;position:relative;z-index:1;display:flex;align-items:center;justify-content:center; }
.avatar-ring img { width:100%;height:100%;border-radius:50%;border:4px solid #0F0F0F;object-fit:cover; }
.orbit { position:absolute;border-radius:50%;border:1px solid rgba(212,160,23,.2);display:flex;align-items:flex-start;justify-content:flex-end; }
.orbit-1 { width:280px;height:280px;animation:orbitSpin 8s linear infinite; }
.orbit-2 { width:350px;height:350px;animation:orbitSpin 12s linear infinite reverse; }
.orbit-dot { width:12px;height:12px;border-radius:50%;background:linear-gradient(135deg,#D4A017,#F0C040);box-shadow:0 0 12px rgba(212,160,23,.8);margin:-6px;transform:translateY(6px); }
.orbit-dot-2 { background:linear-gradient(135deg,#B8860B,#D4A017);box-shadow:0 0 12px rgba(184,134,11,.8); }
@keyframes orbitSpin { to { transform:rotate(360deg); } }

.stat-card { position:absolute;background:rgba(26,26,26,.85);backdrop-filter:blur(20px);border:1px solid rgba(212,160,23,.25);border-radius:20px;padding:1rem 1.25rem;display:flex;align-items:center;gap:.85rem;min-width:150px;box-shadow:0 16px 40px rgba(0,0,0,.5);transition:transform .3s;cursor:default; }
.stat-card:hover { transform:scale(1.05);border-color:rgba(212,160,23,.5); }
.stat-card-icon { font-size:1.8rem; }
.stat-card-num { font-size:1.4rem;font-weight:800;color:#D4A017;line-height:1; }
.stat-card-label { font-size:.75rem;color:rgba(240,237,228,.6);margin-top:.1rem; }
.stat-card-1 { top:5%;right:-5%;animation:floatCard1 4s ease-in-out infinite; }
.stat-card-2 { top:45%;left:-5%;animation:floatCard2 5s ease-in-out infinite; }
.stat-card-3 { bottom:8%;right:5%;animation:floatCard3 3.5s ease-in-out infinite; }
@keyframes floatCard1 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-8px,-12px)} }
@keyframes floatCard2 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(10px,8px)} }
@keyframes floatCard3 { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-6px,-10px)} }

/* Scroll indicator */
.scroll-indicator { position:absolute;bottom:2rem;left:50%;transform:translateX(-50%);display:flex;flex-direction:column;align-items:center;gap:.5rem;color:rgba(212,160,23,.5);font-size:.8rem;z-index:1;animation:fadeIn 1s ease 1s both; }
.scroll-mouse { width:24px;height:38px;border:2px solid rgba(212,160,23,.4);border-radius:12px;display:flex;justify-content:center;padding-top:5px; }
.scroll-wheel { width:4px;height:8px;background:rgba(212,160,23,.6);border-radius:2px;animation:scrollWheel 1.5s ease infinite; }
@keyframes scrollWheel { 0%{opacity:1;transform:translateY(0)} 100%{opacity:0;transform:translateY(12px)} }

/* Animations */
@keyframes floatY { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
@keyframes fadeSlideIn { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
@keyframes fadeIn { from{opacity:0} to{opacity:1} }

/* Desktop responsive */
@media(max-width:900px) {
  .hero-container { grid-template-columns:1fr;padding:100px 1.5rem 60px;gap:2rem; }
  .hero-visual { height:320px;order:-1; }
  .avatar-ring { width:150px;height:150px; }
  .orbit-1 { width:210px;height:210px; }
  .orbit-2 { width:260px;height:260px; }
  .stat-card-1 { top:0;right:0; }
  .stat-card-2 { display:none; }
  .stat-card-3 { bottom:0;right:0; }
  .hero-name { font-size:2.2rem; }
  .hero-actions { flex-direction:column; }
  .hero-actions .btn-hero-primary, .hero-actions .btn-hero-wa { width:100%; }
}
@media(max-width:600px) { .hero-container { padding:80px 1rem 50px; } }

/* ── Show/hide control ── */
.mah { display: none; }

@media (max-width: 768px) {
  .hero { display: none; }
  .mah  { display: flex; }
}

/* ══════════════════════════════════════════
   MOBILE APP HOME SCREEN  (.mah) — Dark Gold
══════════════════════════════════════════ */
.mah {
  flex-direction: column;
  background: #0D0D0D;
  font-family: 'Cairo', 'Almarai', sans-serif;
  direction: rtl;
  overflow-x: hidden;
  animation: mahFadeIn .35s ease both;
}
@keyframes mahFadeIn { from { opacity: 0; } to { opacity: 1; } }

/* ── Greeting banner (auth) ── */
.mah-greeting {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem .85rem;
  background: #1A1A1A;
  border-bottom: 1px solid rgba(212,160,23,.1);
  gap: .75rem;
}
.mah-greeting-text {
  display: flex;
  flex-direction: column;
  gap: .05rem;
}
.mah-hello {
  font-size: .78rem;
  color: rgba(240,237,228,.5);
  font-weight: 500;
}
.mah-name {
  font-size: 1.2rem;
  font-weight: 800;
  color: #F0EDE4;
  line-height: 1.25;
}
.mah-level-chip {
  background: rgba(212,160,23,.1);
  border: 1px solid rgba(212,160,23,.25);
  color: #D4A017;
  border-radius: 99px;
  padding: .28rem .9rem;
  font-size: .75rem;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
}

/* ── Teacher bar (auth) ── */
.mah-teacher-bar {
  display: flex;
  align-items: center;
  gap: .85rem;
  padding: .85rem 1.25rem;
  background: linear-gradient(135deg, #0A0A0A 0%, #111111 60%, #0D0D0D 100%);
  border-bottom: 1px solid rgba(212,160,23,.12);
  position: relative;
  overflow: hidden;
}
.mah-teacher-bar::before {
  content: '';
  position: absolute;
  width: 160px; height: 160px;
  background: radial-gradient(circle, rgba(212,160,23,.2), transparent);
  top: -50px; left: -40px;
  border-radius: 50%;
  pointer-events: none;
}
.mah-tb-avatar {
  width: 52px; height: 52px;
  border-radius: 50%;
  padding: 2.5px;
  background: linear-gradient(135deg, #D4A017, #8B6508);
  flex-shrink: 0;
  box-shadow: 0 0 20px rgba(212,160,23,.4);
  display: flex; align-items: center; justify-content: center;
  position: relative; z-index: 1;
}
.mah-tb-avatar img {
  width: 100%; height: 100%;
  border-radius: 50%;
  border: 2px solid #0F0F0F;
  object-fit: cover;
}
.mah-tb-info { flex: 1; position: relative; z-index: 1; }
.mah-tb-name { font-size: .97rem; font-weight: 800; color: #F0EDE4; line-height: 1.2; }
.mah-tb-sub  { font-size: .72rem; color: rgba(240,237,228,.5); margin-top: .1rem; }
.mah-tb-stats { display: flex; align-items: center; gap: .6rem; flex-shrink: 0; position: relative; z-index: 1; }
.mah-tb-stat  { display: flex; flex-direction: column; align-items: center; gap: .05rem; }
.mah-tb-stat span  { font-size: .92rem; font-weight: 800; color: #D4A017; line-height: 1; }
.mah-tb-stat small { font-size: .6rem; color: rgba(240,237,228,.5); font-weight: 500; }
.mah-tb-divider { width: 1px; height: 28px; background: rgba(212,160,23,.2); }

/* ── Quick access section (auth) ── */
.mah-quick-section { padding: 1.15rem 1rem 1rem; background: #0D0D0D; }
.mah-sec-title {
  font-size: .75rem; font-weight: 700;
  color: rgba(212,160,23,.7);
  text-transform: uppercase; letter-spacing: .06em;
  margin-bottom: .65rem; padding: 0 .2rem;
}
.mah-quick-grid { display: grid; grid-template-columns: 1fr 1fr; gap: .6rem; }
.mah-qcard {
  border-radius: 18px; padding: 1.05rem .9rem;
  display: flex; flex-direction: column; gap: .35rem;
  text-decoration: none; color: #fff;
  position: relative; overflow: hidden;
  min-height: 110px;
  transition: transform .18s ease, box-shadow .18s ease;
  -webkit-tap-highlight-color: transparent;
}
.mah-qcard:active { transform: scale(0.95); }
.mah-qc-lessons { background: linear-gradient(145deg, #D4A017 0%, #8B6508 100%); box-shadow: 0 8px 24px rgba(212,160,23,.3); color: #0F0F0F; }
.mah-qc-exams   { background: linear-gradient(145deg, #1A1A1A 0%, #222 100%); box-shadow: 0 8px 24px rgba(0,0,0,.4); border: 1px solid rgba(212,160,23,.2); color: #F0EDE4; }
.mah-qc-hw      { background: linear-gradient(145deg, #222222 0%, #1A1A1A 100%); box-shadow: 0 8px 24px rgba(0,0,0,.4); border: 1px solid rgba(212,160,23,.15); color: #F0EDE4; }
.mah-qc-results { background: linear-gradient(145deg, #B8860B 0%, #D4A017 100%); box-shadow: 0 8px 24px rgba(184,134,11,.28); color: #0F0F0F; }
.mah-qc-icon  { font-size: 1.65rem; line-height: 1; }
.mah-qc-label { font-size: .88rem; font-weight: 800; line-height: 1.25; }
.mah-qc-sub   { font-size: .7rem; opacity: .82; line-height: 1.4; }
.mah-qc-arrow { font-size: .85rem; opacity: .65; margin-top: auto; align-self: flex-end; transition: transform .18s ease; }
.mah-qcard:active .mah-qc-arrow { transform: translateX(-4px); }

/* ── Guest hero card ── */
.mah-guest-hero {
  background: linear-gradient(135deg, #080808 0%, #111 50%, #0A0A0A 100%);
  position: relative; overflow: hidden; padding: 2.5rem 1.25rem 2rem;
}
.mah-gh-blobs { position: absolute; inset: 0; pointer-events: none; }
.mah-gh-blob-1 {
  position: absolute; width: 280px; height: 280px;
  background: radial-gradient(circle, rgba(212,160,23,.3), transparent);
  top: -80px; right: -60px; border-radius: 50%; filter: blur(40px);
}
.mah-gh-blob-2 {
  position: absolute; width: 220px; height: 220px;
  background: radial-gradient(circle, rgba(184,134,11,.18), transparent);
  bottom: -60px; left: -40px; border-radius: 50%; filter: blur(40px);
}
.mah-gh-math { position: absolute; inset: 0; overflow: hidden; pointer-events: none; display: flex; flex-wrap: wrap; }
.mah-gh-math span {
  position: absolute; font-family: 'Cairo', sans-serif; font-size: 2rem;
  color: rgba(212,160,23,.06); font-weight: 700; user-select: none;
}
.mah-gh-math span:nth-child(1) { top: 8%;  right: 6%; font-size: 2.5rem; }
.mah-gh-math span:nth-child(2) { top: 20%; left: 8%;  font-size: 1.8rem; }
.mah-gh-math span:nth-child(3) { top: 55%; right: 12%; font-size: 2rem; }
.mah-gh-math span:nth-child(4) { top: 65%; left: 5%;  font-size: 2.8rem; }
.mah-gh-math span:nth-child(5) { top: 10%; left: 40%; font-size: 3rem; }
.mah-gh-math span:nth-child(6) { bottom: 15%; right: 35%; font-size: 1.6rem; }
.mah-gh-math span:nth-child(7) { bottom: 8%;  left: 30%; font-size: 2.2rem; }
.mah-gh-math span:nth-child(8) { top: 40%; right: 30%; font-size: 1.5rem; }

.mah-gh-inner { position: relative; z-index: 1; display: flex; flex-direction: column; align-items: center; text-align: center; gap: .65rem; }
.mah-gh-avatar {
  width: 88px; height: 88px; border-radius: 50%; padding: 3px;
  background: linear-gradient(135deg, #D4A017, #8B6508, #F0C040);
  box-shadow: 0 0 40px rgba(212,160,23,.5), 0 16px 40px rgba(0,0,0,.5);
  display: flex; align-items: center; justify-content: center;
  animation: mahFloat 3.5s ease-in-out infinite;
}
.mah-gh-avatar img { width: 100%; height: 100%; border-radius: 50%; border: 3px solid #0F0F0F; object-fit: cover; }
@keyframes mahFloat { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-7px)} }

.mah-gh-badge {
  display: inline-flex; align-items: center; gap: .4rem;
  background: rgba(212,160,23,.1); border: 1px solid rgba(212,160,23,.25);
  border-radius: 99px; padding: .28rem .9rem;
  font-size: .75rem; font-weight: 600; color: rgba(212,160,23,.9);
}
.mah-gh-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: #D4A017; box-shadow: 0 0 6px #D4A017;
  animation: ghDotPulse 1.5s infinite;
}
@keyframes ghDotPulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.5);opacity:.6} }

.mah-gh-name { font-size: 1.55rem; font-weight: 900; color: #F0EDE4; line-height: 1.2; letter-spacing: -.01em; }
.mah-gh-sub {
  font-size: .83rem; color: rgba(240,237,228,.6);
  background: rgba(212,160,23,.08); padding: .3rem 1rem;
  border-radius: 99px; backdrop-filter: blur(8px);
  border: 1px solid rgba(212,160,23,.18);
}
.mah-gh-stats {
  display: flex; align-items: center; gap: .85rem; padding: .75rem 1.5rem;
  background: rgba(212,160,23,.07); border: 1px solid rgba(212,160,23,.18);
  border-radius: 16px; backdrop-filter: blur(12px); margin-top: .25rem;
}
.mah-gh-stat  { display: flex; flex-direction: column; align-items: center; gap: .05rem; }
.mah-gh-stat span  { font-size: 1.15rem; font-weight: 900; color: #D4A017; line-height: 1; }
.mah-gh-stat small { font-size: .65rem; color: rgba(240,237,228,.55); font-weight: 500; }
.mah-gh-stat-div   { width: 1px; height: 30px; background: rgba(212,160,23,.2); }

/* ── Guest CTA section ── */
.mah-guest-cta {
  padding: 1.25rem 1.25rem 1rem; background: #1A1A1A;
  border-bottom: 1px solid rgba(212,160,23,.1);
  display: flex; flex-direction: column; gap: .7rem;
}
.mah-btn {
  display: flex; align-items: center; justify-content: center; gap: .55rem;
  padding: .9rem 1.5rem; border-radius: 99px;
  font-weight: 700; font-size: .97rem; text-decoration: none;
  font-family: 'Cairo', 'Almarai', sans-serif;
  width: 100%; border: none; cursor: pointer;
  transition: opacity .15s ease, transform .15s ease;
  -webkit-tap-highlight-color: transparent;
}
.mah-btn:active { transform: scale(0.97); opacity: .92; }
.mah-btn-primary { background: linear-gradient(135deg, #D4A017, #B8860B); color: #0F0F0F; box-shadow: 0 12px 32px rgba(212,160,23,.35); }
.mah-btn-primary:active { color: #0F0F0F; }
.mah-btn-wa { background: linear-gradient(135deg, #25d366, #128C7E); color: #fff; box-shadow: 0 12px 30px rgba(37,211,102,.25); }
.mah-btn-wa:active { color: #fff; }

.mah-social-proof {
  display: flex; align-items: center; justify-content: center; gap: .55rem;
  font-size: .8rem; color: rgba(240,237,228,.45); padding-top: .1rem;
}
.mah-sp-avatars { display: flex; }
.mah-sp-av {
  width: 26px; height: 26px; border-radius: 50%;
  border: 2px solid rgba(212,160,23,.3);
  display: flex; align-items: center; justify-content: center;
  font-size: .72rem; font-weight: 700; color: #0F0F0F; margin-left: -5px;
}
.mah-sp-av:first-child { margin-left: 0; }
.mah-sp-1 { background: linear-gradient(135deg, #D4A017, #B8860B); }
.mah-sp-2 { background: linear-gradient(135deg, #C89010, #A07008); }
.mah-sp-3 { background: linear-gradient(135deg, #E0B020, #C09010); }

/* ── Features grid (guest) ── */
.mah-features { padding: 1.15rem 1rem 1rem; background: #0D0D0D; }
.mah-feat-grid { display: grid; grid-template-columns: 1fr 1fr; gap: .6rem; }
.mah-feat-card {
  background: #1A1A1A;
  border-radius: 16px; padding: .95rem .9rem;
  display: flex; align-items: flex-start; gap: .65rem;
  border: 1px solid rgba(212,160,23,.12);
  box-shadow: 0 2px 10px rgba(0,0,0,.4);
  transition: transform .18s ease;
  -webkit-tap-highlight-color: transparent;
}
.mah-feat-card:active { transform: scale(0.97); }
.mah-fi {
  width: 38px; height: 38px; border-radius: 11px;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.25rem; flex-shrink: 0;
}
.mah-fi-purple { background: rgba(212,160,23,.12); }
.mah-fi-blue   { background: rgba(212,160,23,.08); }
.mah-fi-green  { background: rgba(34,197,94,.1); }
.mah-fi-orange { background: rgba(245,158,11,.1); }
.mah-feat-label { font-size: .83rem; font-weight: 700; color: #F0EDE4; margin-bottom: .15rem; }
.mah-feat-desc  { font-size: .7rem; color: rgba(240,237,228,.5); line-height: 1.5; }
</style>
